import React, { useCallback, useMemo } from 'react';

// Anything above this is rendered as "999+" - the bubble has no room for more.
const MAX_DISPLAYED_COMMENT_COUNT = 999;

export const formatCommentCount = (count: number): string =>
  count > MAX_DISPLAYED_COMMENT_COUNT ? `${MAX_DISPLAYED_COMMENT_COUNT}+` : String(count);

/**
 * The bubble is a fixed-size glyph, so longer labels have to shrink to fit it.
 * Expressed as a ratio of the icon size so the badge scales as one unit.
 */
export const countFontRatio = (label?: string): number => {
  const length = label ? label.length : 1;

  if (length >= 4) return 0.25;
  if (length === 3) return 0.31;
  if (length === 2) return 0.37;

  return 0.42;
};

interface IParams {
  imageId: string;
  commentCount: number;
  onClick: (imageId: string) => void;
  targetIdPrefix: string;
}

export const useCommentCountBadge = ({
  imageId,
  commentCount,
  onClick,
  targetIdPrefix
}: IParams) => {
  const displayedCount = useMemo(() => formatCommentCount(commentCount), [commentCount]);
  const targetId = useMemo(() => `${targetIdPrefix}_${imageId}`, [targetIdPrefix, imageId]);

  const open = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      onClick(imageId);
    },
    [onClick, imageId]
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        open(event as unknown as React.MouseEvent);
      }
    },
    [open]
  );

  return { displayedCount, targetId, open, onKeyDown };
};
